/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRouter } from "@i18n/next";
import {
  useCompletionCache,
  useCurrentQueueItem,
  usePlayer,
  useProgressCalculation,
} from "@packages/media";
import type { BasePrayerInCollection } from "@packages/sdk";
import {
  Divider,
  IconButton,
  LineItemControlsIcon,
  List,
  ListCollectionDetail,
  numericPixels,
  spacing,
  Text,
  VideoIcon,
} from "@packages/ui";
import React from "react";

const titleStyles: Interpolation = {
  display: "block",
  padding: `${spacing.m} ${spacing.l}`,
};

const dividerStyles: Interpolation = {
  padding: `${spacing.none} ${spacing.l}`,
};

const iconButtonStyles: Interpolation = {
  height: numericPixels[24],
  padding: spacing.none,
  width: numericPixels[24],
};

const listStyles: Interpolation = {
  padding: spacing.s,
};

type PrayersListProps = {
  playSession: (id: number) => void;
  prayers: Partial<BasePrayerInCollection>[];
  sessions: string;
  sessionsAvailable?: boolean;
};

export const PrayersList = ({
  playSession,
  prayers,
  sessions,
  sessionsAvailable,
}: PrayersListProps) => {
  const router = useRouter();
  const { currentItem } = useCurrentQueueItem();
  const player = usePlayer();
  const isPaused = !!player?.paused;
  const progressProps = useProgressCalculation();
  const { completedPrayers } = useCompletionCache();

  return (
    <>
      <Text size="m" type="title" css={titleStyles}>
        {sessions}
      </Text>
      <div css={dividerStyles}>
        <Divider />
      </div>
      <List css={listStyles}>
        {prayers.map((prayer, idx) => (
          <ListCollectionDetail
            description={prayer.detailed_desc}
            disabled={!sessionsAvailable || !prayer.is_available}
            key={`${prayer.title}-${idx}`}
            metaData={[prayer.label_desc, prayer.lengths]}
            metaDataStartIcon={prayer.has_video ? <VideoIcon.Filled /> : null}
            number={idx + 1}
            onClickCapture={(event) => {
              if (prayer.is_song) {
                event.stopPropagation();
                playSession(prayer.id);
              } else {
                router.push(`/prayers/${prayer.id}`);
              }
            }}
            rightActions={[
              <IconButton
                icon={
                  currentItem?.type === "prayer" &&
                  currentItem.prayer.id === prayer.id ? (
                    isPaused ? (
                      <LineItemControlsIcon.Paused {...progressProps} />
                    ) : (
                      <LineItemControlsIcon.Playing {...progressProps} />
                    )
                  ) : prayer.is_completed ||
                    completedPrayers.current.includes(prayer.id) ? (
                    <LineItemControlsIcon.Replay />
                  ) : (
                    <LineItemControlsIcon.Play />
                  )
                }
                key="iconButton"
                onClickCapture={(event) => {
                  event.stopPropagation();
                  playSession(prayer.id);
                }}
                size="m"
                css={iconButtonStyles}
                variant="transparentPrimary"
              />,
            ]}
            title={prayer.title}
          />
        ))}
      </List>
    </>
  );
};
